// Tilecraft calculator: plans or sizes in, one rate per m² out, order placed by the pricing server.
if (!customElements.get('tc-quote')) {
  customElements.define(
    'tc-quote',
    class extends HTMLElement {
      connectedCallback() {
        this.tiles = JSON.parse(this.querySelector('[data-tiles]').textContent);
        this.traditional = JSON.parse(this.querySelector('[data-traditional]').textContent);
        const tradeEl = this.querySelector('[data-trade]');
        this.trade = tradeEl ? JSON.parse(tradeEl.textContent) : null;
        this.api = this.dataset.api.replace(/\/$/, '');
        this.loadRates();
        this.form = this.querySelector('form');
        this.money = new Intl.NumberFormat('en-NZ', { style: 'currency', currency: this.dataset.currency || 'NZD', currencyDisplay: 'narrowSymbol', maximumFractionDigits: 0 });
        this.form.addEventListener('input', (e) => { if (!e.target.closest('[data-plans]')) this.update(); });
        this.querySelector('[data-plan-file]')?.addEventListener('change', (e) => {
          const file = e.target.files[0];
          this.querySelector('[data-plan-name]').textContent = file ? file.name : 'Drop your plans here';
          this.readPlans(file);
        });
        this.querySelectorAll('[data-step]').forEach((b) =>
          b.addEventListener('click', () => {
            const input = b.parentElement.querySelector('input');
            input.value = Math.max(Number(input.min) || 0, (Number(input.value) || 0) + Number(b.dataset.step));
            this.touched = true;
            this.update();
          })
        );
        this.form.addEventListener('change', () => this.update());
        this.form.addEventListener('submit', (e) => this.order(e));
        this.querySelectorAll('[data-tab]').forEach((t) =>
          t.addEventListener('click', () => {
            this.querySelectorAll('[data-tab]').forEach((x) => x.setAttribute('aria-selected', String(x === t)));
            this.querySelectorAll('[data-pane]').forEach((p) => (p.hidden = p.dataset.pane !== t.dataset.tab));
          })
        );
        this.form.addEventListener('input', () => { this.touched = true; });
        const preset = new URLSearchParams(location.search).get('tile');
        const presetInput = preset && this.querySelector(`[name="tc-tile"][value="${CSS.escape(preset)}"]`);
        if (presetInput) presetInput.checked = true;
        const tileName = this.querySelector('[data-tile-name]');
        const showTile = (input) => {
          const pick = input || this.querySelector('[name="tc-tile"]:checked');
          if (tileName && pick) tileName.textContent = pick.dataset.title;
        };
        this.querySelectorAll('[name="tc-tile"]').forEach((r) => {
          r.addEventListener('change', () => showTile());
          r.closest('label').addEventListener('mouseenter', () => showTile(r));
          r.closest('label').addEventListener('mouseleave', () => showTile());
        });
        showTile();
        this.querySelector('[data-h-use]').addEventListener('click', () => {
          this.querySelector('[data-area]').value = this.helperArea();
          this.querySelector('.tc-helper').open = false;
          this.update();
        });
        this.update();
      }

      // Tiled area from room size, same rules as the coverage drawings:
      // full = floor + all walls less one door; half = walls to 1.2 m plus the shower to the ceiling;
      // shower = floor + the two shower walls; floor = floor.
      helperArea() {
        const v = (k) => Math.max(0, parseFloat(this.querySelector(`[data-h="${k}"]`).value) || 0);
        const L = v('length'), W = v('width'), H = v('height') || 2.4;
        const showerWalls = v('shower_w') + v('shower_d');
        const floor = L * W, perimeter = 2 * (L + W), half = Math.min(1.2, H);
        const door = { w: 0.81, h: 1.98 };
        const coverage = this.querySelector('[name="tc-coverage"]:checked').value;
        const area = {
          full: floor + perimeter * H - door.w * Math.min(door.h, H),
          half: floor + perimeter * half - door.w * half + showerWalls * (H - half),
          shower: floor + showerWalls * H,
          floor: floor,
        }[coverage];
        return Math.round(area * 10) / 10;
      }

      // Plans go to a private store, get read, and come back as rooms with tiled m² per option.
      async readPlans(file) {
        const box = this.querySelector('[data-plans]');
        const status = box.querySelector('[data-plan-status]');
        const roomsEl = box.querySelector('[data-plan-rooms]');
        const say = (text, isError) => {
          status.hidden = false;
          status.textContent = text;
          status.classList.toggle('tc-plans__status--error', !!isError);
        };
        roomsEl.hidden = true;
        if (!file) return;
        if (file.size > 50 * 1024 * 1024) return say('That file is over 50 MB. Try a smaller PDF, or enter your m² below.', true);

        const endpoint = box.dataset.endpoint.replace(/\/$/, '');
        this.lock(true);
        try {
          say('Uploading your plans…');
          const { upload } = await import('https://cdn.jsdelivr.net/npm/@vercel/blob@2.8.2/client/+esm');
          const blob = await upload(file.name, file, { access: 'private', handleUploadUrl: `${endpoint}/api/upload` });
          say('Reading your plans. This takes two to four minutes.');
          const res = await fetch(`${endpoint}/api/measure`, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ url: blob.url }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.error);
          const rooms = (data.rooms || []).filter((r) => !r.areas.error);
          if (!rooms.length) throw new Error('No room sizes found');
          status.hidden = true;
          this.showRooms(rooms);
        } catch (err) {
          say("We couldn't read sizes off those plans. Enter your m² below, or work it out from your room size.", true);
        } finally {
          this.lock(false);
        }
      }

      lock(reading) {
        const button = this.querySelector('[data-order]');
        this.reading = reading;
        button.disabled = reading || !this.rates;
        button.textContent = reading ? 'Reading your plans…' : button.dataset.label;
      }

      // Room sizes in metres from what the plan reader returned, with its defaults where the plans are silent.
      roomGeometry(room) {
        const mm = (v) => (v ? v / 1000 : 0);
        const height = mm(room.ceiling_height_mm) || 2.4;
        const half = Math.min(1.2, height);
        const w = mm(room.floor_width_mm), l = mm(room.floor_length_mm);
        const perimeter = mm(room.perimeter_mm) || (w && l ? 2 * (w + l) : 0);
        const shower = room.shower?.present ? (room.shower.wall_lengths_mm || []).reduce((a, b) => a + b, 0) / 1000 : 0;
        const doors = (room.doors || []).map((d) => ({ w: mm(d.width_mm), h: mm(d.height_mm) || 1.98 }));
        return {
          floor: room.areas.floor_m2,
          height,
          half,
          perimeter,
          shower,
          longestWall: Math.max(w, l) || 0,
          doorsHalf: doors.reduce((a, d) => a + d.w * Math.min(d.h, half), 0),
          doorsFull: doors.reduce((a, d) => a + d.w * Math.min(d.h, height), 0),
        };
      }

      showRooms(rooms) {
        const roomsEl = this.querySelector('[data-plan-rooms]');
        const wet = ['bathroom', 'ensuite'];
        roomsEl.replaceChildren();

        const heading = document.createElement('p');
        heading.className = 'tc-plans__heading';
        heading.textContent = 'Rooms on your plans. Choose what to tile in each.';
        roomsEl.append(heading);

        const notes = new Set();
        rooms.forEach((room) => {
          (room.areas.assumed || []).forEach((n) => notes.add(n));
          const g = this.roomGeometry(room);
          const isWet = wet.includes(room.type);
          const row = document.createElement('fieldset');
          row.className = 'tc-plans__room';
          row.dataset.shower = g.shower > 0 ? '1' : '0';
          row.dataset.wet = isWet ? '1' : '0';

          const legend = document.createElement('legend');
          const include = document.createElement('input');
          include.type = 'checkbox';
          include.checked = isWet;
          include.dataset.include = '';
          const nameLabel = document.createElement('label');
          nameLabel.append(include, ` ${room.name}`);
          legend.append(nameLabel);
          row.append(legend);

          const tick = (label, key, checked) => {
            const l = document.createElement('label');
            const box = document.createElement('input');
            box.type = 'checkbox';
            box.checked = checked;
            box.dataset.surface = key;
            l.append(box, ` ${label}`);
            return l;
          };
          const surfaces = document.createElement('div');
          surfaces.className = 'tc-plans__surfaces';
          surfaces.append(tick(`Floor (${g.floor.toFixed(1)} m²)`, 'floor', true));
          if (g.shower > 0) surfaces.append(tick('Shower walls to the ceiling', 'shower', isWet));

          const walls = document.createElement('select');
          walls.dataset.walls = '';
          walls.setAttribute('aria-label', `Other walls in the ${room.name}`);
          walls.add(new Option('Other walls: none', 'none'));
          if (g.perimeter) {
            walls.add(new Option('Other walls: half height', 'half'));
            walls.add(new Option('Other walls: full height', 'full'));
          }
          if (g.longestWall) walls.add(new Option(`One wall, full height (${g.longestWall.toFixed(1)} m wall)`, 'one'));

          const result = document.createElement('span');
          result.className = 'tc-plans__room-m2';
          row.append(surfaces, walls, result);

          // m² for this room from the ticked surfaces; same sums as the plan reader's presets.
          const calc = () => {
            const on = (k) => row.querySelector(`[data-surface="${k}"]`)?.checked;
            let m2 = on('floor') ? g.floor : 0;
            const showerOn = on('shower');
            if (showerOn) m2 += g.shower * g.height;
            const otherRun = Math.max(0, (g.perimeter || 0) - (showerOn ? g.shower : 0));
            m2 += {
              none: 0,
              half: otherRun * g.half - g.doorsHalf,
              full: otherRun * g.height - g.doorsFull,
              one: (g.longestWall || 0) * g.height,
            }[walls.value];
            row.dataset.m2 = Math.max(0, Math.round(m2 * 10) / 10);
            result.textContent = `${row.dataset.m2} m² of tile`;
            row.classList.toggle('tc-plans__room--off', !include.checked);
          };
          row.addEventListener('change', calc);
          calc();
          roomsEl.append(row);
        });

        const total = document.createElement('div');
        total.className = 'tc-plans__total';
        const sum = document.createElement('strong');
        total.append(sum);
        roomsEl.append(total);

        if (notes.size) {
          const note = document.createElement('p');
          note.className = 'tc-plans__note';
          note.textContent = `${[...notes].join('. ')}. We check every size when we measure.`;
          roomsEl.append(note);
        }

        // Rooms ticked here set the m² and the room counts below, so the price follows the plans.
        // A recess is assumed per shower; the customer can change any count.
        const apply = () => {
          let m2 = 0, bathrooms = 0, showers = 0;
          roomsEl.querySelectorAll('.tc-plans__room').forEach((row) => {
            if (!row.querySelector('[data-include]').checked) return;
            m2 += parseFloat(row.dataset.m2);
            if (row.dataset.wet === '1') bathrooms += 1;
            if (row.dataset.shower === '1') showers += 1;
          });
          m2 = Math.round(m2 * 10) / 10;
          this.planRooms = [...roomsEl.querySelectorAll('.tc-plans__room')]
            .filter((row) => row.querySelector('[data-include]').checked)
            .map((row) => ({
              name: row.querySelector('legend').textContent.trim(),
              summary: [
                ...[...row.querySelectorAll('[data-surface]:checked')].map((b) => b.parentElement.textContent.trim().replace(/ \(.*\)$/, '')),
                row.querySelector('[data-walls]').selectedOptions[0].textContent,
                `${row.dataset.m2} m²`,
              ].join(', '),
            }));
          sum.textContent = `${m2} m² of tile, ${bathrooms} bathroom${bathrooms === 1 ? '' : 's'}, ${showers} shower${showers === 1 ? '' : 's'}`;
          this.querySelector('[data-area]').value = m2 || 1;
          this.querySelector('[data-count="bathroom"]').value = Math.max(1, bathrooms);
          this.querySelector('[data-count="shower"]').value = showers;
          this.querySelector('[data-count="recess"]').value = showers;
          this.update();
        };
        roomsEl.addEventListener('change', apply);
        apply();
        roomsEl.hidden = false;
      }

      // Rates come from the pricing server: retail, or trade for a signed-in trade buyer.
      async loadRates() {
        try {
          const res = await fetch(`${this.api}/api/rates`, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ trade: this.trade }),
          });
          if (!res.ok) throw new Error('rates');
          this.rates = (await res.json()).rates;
        } catch (err) {
          this.querySelector('[data-detail]').textContent = 'Pricing is unavailable right now. Please try again shortly.';
          return;
        }
        this.lock(!!this.reading);
        this.update();
      }

      // Same sum as the server: whole m² and each room's items folded into one rate per m².
      price({ m2, bathrooms, showers, recesses }) {
        const r = this.rates;
        const q = Math.max(1, Math.ceil(m2 - 1e-9));
        const cost = q * r.m2 + bathrooms * r.bathroom + showers * r.shower + recesses * r.recess + r.order;
        const rate = Math.ceil(cost / q);
        return { m2: q, rate, total: rate * q };
      }

      job() {
        const f = this.form;
        const count = (k) => Math.max(0, Math.round(parseFloat(this.querySelector(`[data-count="${k}"]`).value) || 0));
        return {
          tile: f.querySelector('[name="tc-tile"]:checked').value,
          m2: Math.max(1, parseFloat(f.querySelector('[data-area]').value) || 1),
          bathrooms: Math.max(1, count('bathroom')),
          showers: count('shower'),
          recesses: count('recess'),
        };
      }

      update() {
        this.querySelector('[data-h-result]').textContent = this.helperArea();
        if (!this.rates) return;
        const job = this.job();
        const p = this.price(job);
        this.querySelector('[data-total]').textContent = this.money.format(p.total);
        this.querySelector('[data-detail]').textContent = this.touched || this.planRooms
          ? `${p.m2} m² at ${this.money.format(p.rate)} per m²`
          : `Example: 2 bathrooms, ${p.m2} m²`;

        const compare = this.querySelector('[data-compare]');
        const t = this.traditional;
        if (compare && t.m2 > 0) {
          const traditional = job.m2 * t.m2 + job.bathrooms * t.bathroom + job.showers * t.shower + job.recesses * t.recess + t.job;
          const saving = traditional - p.total;
          compare.textContent = saving > 0
            ? `${this.money.format(Math.round(saving))} less than tiling the traditional way.`
            : '';
        }

        document.querySelectorAll('[data-comparable]').forEach((card) => {
          const c = JSON.parse(card.dataset.comparable);
          const ours = this.price({ m2: c.m2, bathrooms: c.bathrooms, showers: c.showers, recesses: c.recesses }).total;
          const saving = c.traditional - ours;
          card.querySelector('[data-ours]').textContent = this.money.format(ours);
          card.querySelector('[data-saving]').textContent = saving > 0 ? `${Math.round((saving / c.traditional) * 100)}%` : '–';
        });
      }

      // The server prices the job again and makes the order: checkout to pay (retail),
      // or checkout that places it at the locked price, payment due on delivery (trade).
      async order(e) {
        e.preventDefault();
        const button = this.querySelector('[data-order]');
        const say = this.querySelector('[data-detail]');
        button.disabled = true;
        button.textContent = 'One moment…';
        try {
          const res = await fetch(`${this.api}/api/order`, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
              ...this.job(),
              rooms: this.planRooms || [],
              startDate: this.querySelector('[data-start]').value || null,
              trade: this.trade,
            }),
          });
          const data = await res.json();
          if (!res.ok || !data.url) throw new Error(data.error || 'order');
          location.href = data.url;
        } catch (err) {
          say.textContent = "We couldn't start your order. Please try again, or contact us.";
          button.disabled = false;
          button.textContent = button.dataset.label;
        }
      }
    }
  );
}
